import React, { useEffect } from "react";
import {
  Sidebar,
  BaseButton,
  Input,
  confirmDialog,
} from "@velocity-ehs/component-library";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import classes from "./CustomerApplicationEditSlider.module.scss";
import { CustomerApplicationActions } from "../../../hooks/useCustomerApplicationMutation";
import classNames from "classnames/bind";

export interface ApplicationEditForm {
  name: string;
  solutionName: string;
  applicationUrl: string;
}

interface Props {
  application: any;
  showSlider: boolean;
  onClose: () => void;
  onMutate: (action: CustomerApplicationActions, values: any) => void;
  isLoading: { [key: string]: boolean };
}

const CustomerApplicationEditSlider: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const cx = classNames.bind(classes);

  const formMethods = useForm<ApplicationEditForm>({
    mode: "onBlur",
  });

  const { reset, control, getValues } = formMethods;

  const onSubmit = async (action: CustomerApplicationActions) => {
    try {
      const applicationUrl = getValues("applicationUrl");
      await props.onMutate(action, {
        id: props.application.id,
        applicationUrl,
      });
    } catch (error) {}
  };

  const confirmUnassign = () => {
    confirmDialog({
      message: t("customer_application_unassign_warning_message"),
      header: t("customer_application_unassign_warning_header"),
      accept: () => onSubmit(CustomerApplicationActions.UNASSIGN),
      reject: () => props.onClose(),
      acceptLabel: t("customer_application_unassign"),
      rejectLabel: t("universal_no"),
    });
  };

  useEffect(() => {
    if (!props.showSlider || !props.application) return;
    reset({
      name: props.application.name,
      solutionName: props.application.productLine?.name,
      applicationUrl: props.application.applicationUrl,
    });
  }, [props.showSlider, props.application, reset]);

  const footerButtons = () => {
    return (
      <>
        {props.application?.isAssigned ? (
          <>
            <BaseButton
              loading={props.isLoading.unassign}
              disabled={props.isLoading.any}
              label={t("customer_application_unassign")}
              onClick={confirmUnassign}
              className={classes.sliderActions}
            />
            <BaseButton
              loading={props.isLoading.edit}
              disabled={props.isLoading.any}
              label={t("universal_Save")}
              onClick={() => onSubmit(CustomerApplicationActions.EDIT)}
              className={cx(classes.sliderActions, "p-button-text")}
            />
          </>
        ) : (
          <BaseButton
            loading={props.isLoading.assign}
            disabled={props.isLoading.any}
            label={t("customer_application_assign")}
            onClick={() => onSubmit(CustomerApplicationActions.ASSIGN)}
            className={classes.sliderActions}
          />
        )}

        <BaseButton
          label={t("universal_Cancel")}
          className="p-button-text"
          onClick={props.onClose}
        />
      </>
    );
  };

  return (
    <Sidebar
      header={t("customer_application_edit_header")}
      visible={props.showSlider}
      onHide={props.onClose}
      footer={footerButtons()}
    >
      <form id="customerApplicationForm">
        <Input
          control={control}
          label={t("universal_Name")}
          name="name"
          readOnly
        />
        <Input
          control={control}
          label={t("customer_application_edit_solution")}
          name="solutionName"
          readOnly
        />
        <Input
          control={control}
          label={t("customer_applications_application_url")}
          name="applicationUrl"
        />
      </form>
    </Sidebar>
  );
};

export default CustomerApplicationEditSlider;
